import { KeyboardEvent, useState } from 'react';
import { Box, IconButton as MuiIconButton, Stack, styled, Typography } from '@mui/material';
import { IndicatorButtons } from './IndicatorButtons';
import { PreviousButton } from './PreviousButton';
import { NextButton } from './NextButton';
import { CarouselPreviewProps } from './CarouselPreview';
import { NoImageElement } from './NoImageElement';
import { motion, AnimatePresence } from 'framer-motion';
import { Image } from '@retail/components';
import { useProductsTFunction } from '@retail/products/i18n';

export interface CarouselItem {
  url: string;
  description: string;
}

interface Props
  extends Pick<
    CarouselPreviewProps,
    'activeSlide' | 'items' | 'handlePreviousClick' | 'handleNextClick'
  > {
  height?: number;
  maxWidth?: number;
  fallbackElement?: JSX.Element;
}

export function MgCarousel({
  activeSlide,
  handlePreviousClick,
  handleNextClick,
  fallbackElement,
  items,
  height = 295,
  maxWidth = 600,
}: Props) {
  const t = useProductsTFunction();

  const hasItems = items?.length > 0;
  const hasMultipleItems = items?.length > 1;
  const imageHeight = items?.length > 0 ? height : 200;

  const [prevSlide, setPrevSlide] = useState(activeSlide);
  const [direction, setDirection] = useState(0);
  if (activeSlide !== prevSlide) {
    setDirection(activeSlide > prevSlide ? 1 : -1);
    setPrevSlide(activeSlide);
  }

  const goTo = (index: number) => {
    if (index < activeSlide && index >= 0) handlePreviousClick(index);
    if (index > activeSlide && index < items.length) handleNextClick(index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!hasMultipleItems) return;
    if (event.key === 'ArrowLeft') goTo(activeSlide - 1);
    if (event.key === 'ArrowRight') goTo(activeSlide + 1);
  };

  return (
    <Stack display="flex" height="calc(100% - 40px)" justifyContent="center" alignItems="center">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          width: '100%',
          position: 'relative',
          maxWidth: maxWidth,
          height: imageHeight,
          maxHeight: imageHeight,
          padding: 0,
          margin: 0,
          overflow: 'hidden',
          outline: 'none',
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: -2,
          },
        }}
        tabIndex={hasMultipleItems ? 0 : undefined}
        onKeyDown={handleKeyDown}
      >
        {hasItems ? (
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={activeSlide}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ x: { type: 'spring', stiffness: 300, damping: 30 } }}
              drag={hasMultipleItems ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_, { offset, velocity }) => {
                const swipe = offset.x * velocity.x;
                if (offset.x < 0 && swipe < -swipeThreshold) goTo(activeSlide + 1);
                else if (offset.x > 0 && swipe > swipeThreshold) goTo(activeSlide - 1);
              }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: hasMultipleItems ? 'grab' : undefined,
              }}
            >
              <Image
                alt={items[activeSlide].description}
                height={imageHeight}
                draggable={false}
                sx={{
                  height: imageHeight,
                  maxHeight: imageHeight,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  userSelect: 'none',
                }}
                src={items[activeSlide].url}
                fallback={() => fallbackElement ?? <NoImageElement isPreview={false} />}
              />
            </motion.div>
          </AnimatePresence>
        ) : (
          <NoImageElement isPreview={false} noImageLabel={t('products.mgItem.notFound.noImage')} />
        )}

        {hasMultipleItems && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              width: '100%',
              transform: 'translateY(-50%)',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '0 10px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                transform: 'translateY(-50%)',
              }}
            >
              <PreviousButton activeSlide={activeSlide} setActiveSlide={handlePreviousClick} />
            </Box>

            <Box
              sx={{
                position: 'absolute',
                right: 0,
                transform: 'translateY(-50%)',
              }}
            >
              <NextButton
                items={items}
                activeSlide={activeSlide}
                setActiveSlide={handleNextClick}
              />
            </Box>
          </Box>
        )}
      </Box>

      {hasMultipleItems && (
        <Typography variant="body2" color="text.secondary" mt={1}>
          {t('products.mgItem.imageCount', { current: activeSlide + 1, total: items.length })}
        </Typography>
      )}

      <Box mb={1}>
        <IndicatorButtons
          items={items}
          activeSlide={activeSlide}
          setActiveSlide={handlePreviousClick}
        />
      </Box>
    </Stack>
  );
}

const swipeThreshold = 10000;

const slideVariants = {
  enter: (direction: number) => ({ x: direction >= 0 ? '100%' : '-100%' }),
  center: { x: 0 },
  exit: (direction: number) => ({ x: direction >= 0 ? '-100%' : '100%' }),
};

export const IconButton = styled(MuiIconButton)(() => ({
  '&:disabled': {
    opacity: 0.25,
  },
  color: 'inherit',
  fontSize: '3rem',
}));
